/// <amd-module name="VC/VersionControlSelector/VersionControlSelector" />
import * as React from "react";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Icon } from "VSSUI/Icon";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { getId } from "VSS/Core/Util/Object";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { VssComponent, Components, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import * as Resources from "VC/VersionControlSelector/Resources";
import { Location } from "VSSUI/Utilities/Position";
import { IListBoxItem } from "VSSUI/ListBox";

interface VersionControlTypeDescriptor {
    id: string;
    name: string;
    description: string;
    isDefault: boolean;
    isDisabled: boolean;
}

interface VersionControlSelectorState {
    versionControlTypes: IListBoxItem[];
    selectedKey: string;
}

interface VersionControlSelectorProps extends IVssComponentProperties {
    onSelectedOptionChanged: (key: string, value: string) => void;
    initialOptions?: { [key: string]: string };
    disabled?: boolean;
}

class VersionControlSelector extends VssComponent<VersionControlSelectorProps, VersionControlSelectorState> {
    private static PROVIDER_ID = "ms.vss-code-web.vc-versioncontrol-types-provider";
    private static VERSION_CONTROL_SELECTOR_KEY = "versionControlSelector";

    private _dropdownId: string;
    private _helpTooltipId: string;
    private _versionControlSelection = new DropdownSelection();

    constructor(props: VersionControlSelectorProps, context: IVssComponentContext) {
        super(props, context);

        this._dropdownId = this.props.id || getId("drop-down");
        this._helpTooltipId = getId("help-tooltip");
        this._versionControlSelection.select(0);

        this.state = {
            versionControlTypes: [],
            selectedKey: "",
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._loadVersionControlTypes();
    }

    public render(): JSX.Element {
        return <div className={this.props.className}>
            {
                this.state.versionControlTypes.length > 0 &&
                <>
                    <div className="flex flex-center">
                        <FormItem label={Resources.VersionControlSelectorLabel} className="vcs-dropdown-label">
                            <Link className="vcs-help-icon-container"
                                ariaLabel={Resources.VersionControlLearnMoreAriaLabel}
                                ariaDescribedBy={this._helpTooltipId}
                                href={Resources.VersionControlLearnMoreLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={this.props.disabled}
                                tooltipProps={{
                                    anchorOffset: { horizontal: 0, vertical: -10 },
                                    anchorOrigin: { horizontal: Location.center, vertical: Location.start },
                                    id: this._helpTooltipId,
                                    showOnFocus: true,
                                    text: Resources.VersionControlHelpTooltip,
                                    tooltipOrigin: { horizontal: Location.center, vertical: Location.end }
                                }}
                            >
                                <Icon iconName="Unknown" />
                            </Link>
                        </FormItem>
                    </div>
                    <Dropdown
                        className={"vc-selector-dropdown"}
                        ariaLabel={Resources.VersionControlDropdownAriaLabel}
                        disabled={this.props.disabled}
                        selection={this._versionControlSelection}
                        onSelect={this._onChanged}
                        items={this.state.versionControlTypes}
                        calloutContentClassName={"vc-selector-callout"}
                    />
                </>
            }
        </div>;
    }

    private _onChanged = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, option: IListBoxItem): void => {
        const selectedKey = option.id.toString();
        this.props.onSelectedOptionChanged(VersionControlSelector.VERSION_CONTROL_SELECTOR_KEY, selectedKey);
        this.setState({ selectedKey: selectedKey });
    }

    private _loadVersionControlTypes(): void {
        this.trackPromise(this.context.pageContext.getService<IVssContributionService>("IVssContributionService")
            .getDataAsync<VersionControlTypeDescriptor[]>(VersionControlSelector.PROVIDER_ID)).promise
            .then((versionControlTypesData: VersionControlTypeDescriptor[] | undefined) => {
                if (versionControlTypesData) {
                    const versionControlTypes: IListBoxItem[] = [];
                    let defaultSelectedKey: string | null = null;
                    if (this.props.initialOptions) {
                        defaultSelectedKey = this.props.initialOptions[VersionControlSelector.VERSION_CONTROL_SELECTOR_KEY];
                    }
                    let defaultVersionControlType: string = "";
                    versionControlTypesData.map((item, index) => {
                        if (item.name) {
                            versionControlTypes.push({
                                id: item.id,
                                text: item.name,
                            });

                            if ((!defaultSelectedKey && item.isDefault) || (item.id === defaultSelectedKey)) {
                                defaultVersionControlType = item.id;
                            }
                        }
                    });

                    if (!defaultVersionControlType && versionControlTypes.length > 0) {
                        defaultVersionControlType = versionControlTypes[0].id.toString();
                    }

                    this.setState({
                        versionControlTypes: versionControlTypes,
                        selectedKey: defaultVersionControlType,
                    });

                    (defaultSelectedKey !== defaultVersionControlType)
                        && this.props.onSelectedOptionChanged(VersionControlSelector.VERSION_CONTROL_SELECTOR_KEY, defaultVersionControlType);
                }
            }, () => {
                // no-op
            });
    }
}

Components.add("VC.VersionControlSelector", VersionControlSelector);
